import type { JSX } from "preact";
import { useMemo, useState } from "preact/compat";
import { sweCompare } from "@gss/common/sweCompare";
import { uniqFilter } from "@gss/common/uniqFilter";
import { Col } from "@gss/components/Col";
import { InputText } from "@gss/components/InputText";
import { Row } from "@gss/components/Row";
import { Select } from "@gss/components/Select";
import type { VirtualMeasurement } from "@gss/types/VirtualMeasurement";

type SortOrder = "name-period" | "period-name";

interface Props {
    sortOrder: SortOrder;
    measurements: VirtualMeasurement[];
    render: (measurements: VirtualMeasurement[]) => JSX.Element;
}

interface Filters {
    text?: string;
    name?: string;
    period?: string;
}

export function MeasurementFilter({
    measurements,
    sortOrder,
    render,
}: Props): JSX.Element | null {
    const [filters, setFilters] = useState<Filters>({});

    const names = useMemo(() => {
        return measurements
            .map((m) => m.name)
            .filter(uniqFilter())
            .toSorted(sweCompare);
    }, [measurements]);

    const periods = useMemo(() => {
        return measurements
            .map((m) => m.period)
            .filter(uniqFilter())
            .toSorted()
            .toReversed();
    }, [measurements]);

    const sortedMeasurements = useMemo(() => {
        return getSortedCopy(measurements, sortOrder);
    }, [measurements, sortOrder]);

    const filteredMeasurements = useMemo(() => {
        return getFilteredMeasurements(sortedMeasurements, filters);
    }, [sortedMeasurements, filters]);

    return (
        <div>
            <section role="search" aria-label="Mätningsfilter">
                <Row>
                    <Col>
                        <InputText
                            aria-label="Namn på mätning"
                            autoFocus
                            value={filters.text}
                            placeholder="Fritext"
                            onChangeOpt={(text) =>
                                setFilters((filters) => ({
                                    ...filters,
                                    text,
                                }))
                            }
                        />
                    </Col>

                    <Col>
                        <Select
                            value={filters.name}
                            onChange={(name) =>
                                setFilters((filters) => ({ ...filters, name }))
                            }
                        >
                            {[
                                { value: undefined, children: "Välj mätning" },
                                ...names.map((name) => ({
                                    value: name,
                                    children: name,
                                })),
                            ]}
                        </Select>
                    </Col>

                    <Col xs="2">
                        <Select
                            value={filters.period}
                            onChange={(period) =>
                                setFilters((filters) => ({
                                    ...filters,
                                    period,
                                }))
                            }
                        >
                            {[
                                { value: undefined, children: "Välj period" },
                                ...periods.map((period) => ({
                                    value: period,
                                    children: period,
                                })),
                            ]}
                        </Select>
                    </Col>
                </Row>
            </section>

            <section aria-label="Filtrerade mätningar">
                {render(filteredMeasurements)}
            </section>
        </div>
    );
}

function getSortedCopy(
    measurements: VirtualMeasurement[],
    sortOrder: SortOrder,
): VirtualMeasurement[] {
    if (sortOrder === "name-period") {
        return measurements.toSorted((a, b) => {
            return (
                sweCompare(a.name, b.name) || b.period.localeCompare(a.period)
            );
        });
    }
    return measurements.toSorted((a, b) => {
        return b.period.localeCompare(a.period) || sweCompare(a.name, b.name);
    });
}

function getFilteredMeasurements(
    measurements: VirtualMeasurement[],
    filters: Filters,
): VirtualMeasurement[] {
    const lcText = filters.text?.toLowerCase();
    return measurements.filter((m) => {
        if (filters.name != null && filters.name !== m.name) {
            return false;
        }
        if (filters.period != null && filters.period !== m.period) {
            return false;
        }
        if (lcText != null && !m.name.toLowerCase().includes(lcText)) {
            return false;
        }
        return true;
    });
}
